import { z } from 'zod';

const inventoryItemSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('fish'), fishId: z.string(), weight: z.number().nonnegative() }),
  z.object({ type: z.literal('bycatch'), bycatchId: z.string() }),
]);

const vesselInstanceSchema = z.object({
  vesselId: z.string(),
  name: z.string(),
});

const activeVesselTripSchema = z.object({
  vesselId: z.string(),
  name: z.string(),
  endTimestamp: z.number(),
});

const skillLevelSchema = z.object({
  level: z.number().int().positive(),
});

const equipmentInstanceSchema = z.object({
  id: z.string(),
  level: z.number().int().positive(),
});

const propertyInstanceSchema = z.object({
  id: z.string(),
  tier: z.number().int().min(1).max(5),
});

const playerStatsSchema = z.object({
  level: z.number().int().positive(),
  xp: z.number().nonnegative(),
  xpToNext: z.number().positive(),
  unallocatedPoints: z.number().int().nonnegative(),
  skills: z.object({
    fishing: skillLevelSchema,
    bartering: skillLevelSchema,
    business: skillLevelSchema,
    luck: skillLevelSchema,
  }),
});

export const gameStateSchema = z.object({
  version: z.number().int().positive(),
  coins: z.number().nonnegative(),
  totalCoinsEarned: z.number().nonnegative(),
  fishCaught: z.number().int().nonnegative(),
  inventory: z.array(inventoryItemSchema),
  vessels: z.array(vesselInstanceSchema),
  vesselTrips: z.array(activeVesselTripSchema),
  lastTickTimestamp: z.number(),
  currentLocation: z.string(),
  discoveredLocations: z.array(z.string()),
  isFishing: z.boolean(),
  fishingStartTime: z.number().nullable(),
  fishingGeneration: z.number().int().nonnegative(),
  playerStats: playerStatsSchema,
  equipment: z.array(equipmentInstanceSchema),
  properties: z.array(propertyInstanceSchema),
});

export type ValidatedGameState = z.infer<typeof gameStateSchema>;

export function validateGameState(data: unknown): ValidatedGameState | null {
  const result = gameStateSchema.safeParse(data);
  if (result.success) {
    return result.data;
  }
  return null;
}

function createDefaultPlayerStats(): z.infer<typeof playerStatsSchema> {
  return {
    level: 1, xp: 0, xpToNext: 100, unallocatedPoints: 0,
    skills: {
      fishing: { level: 1 },
      bartering: { level: 1 },
      business: { level: 1 },
      luck: { level: 1 },
    },
  };
}

function createDefaultEquipment(): z.infer<typeof equipmentInstanceSchema>[] {
  return [
    { id: 'rod', level: 1 },
    { id: 'boat', level: 1 },
    { id: 'bait', level: 1 },
    { id: 'line', level: 1 },
  ];
}

export function migrateV1ToV2(state: Record<string, unknown>): Record<string, unknown> {
  return {
    ...state,
    version: 2,
    currentLocation: 'home',
    discoveredLocations: ['home'],
    isFishing: false,
    fishingStartTime: null,
    fishingGeneration: 0,
  };
}

export function migrateV2ToV3(state: Record<string, unknown>): Record<string, unknown> {
  return {
    ...state,
    version: 3,
    playerStats: createDefaultPlayerStats(),
    equipment: createDefaultEquipment(),
  };
}

export function migrateV3ToV4(state: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = { ...state, version: 4 };

  if (state.playerStats && typeof state.playerStats === 'object') {
    const ps = state.playerStats as Record<string, unknown>;
    const skills = ps.skills as Record<string, unknown> | undefined;
    if (skills && typeof skills === 'object') {
      const newSkills: Record<string, unknown> = {};
      for (const key of Object.keys(skills)) {
        const sk = skills[key] as Record<string, unknown> | undefined;
        if (sk && typeof sk === 'object') {
          newSkills[key] = { level: sk.level ?? 1 };
        }
      }
      result.playerStats = { ...ps, skills: newSkills };
    }
  }

  if (Array.isArray(state.equipment)) {
    result.equipment = (state.equipment as Record<string, unknown>[]).map(eq => ({
      id: eq.id as string,
      level: (eq.level as number) ?? 1,
    }));
  }

  return result;
}

export function migrateV4ToV5(state: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = { ...state, version: 5 };
  result.properties = [{ id: 'home', tier: 1 }];

  if (Array.isArray(result.equipment)) {
    result.equipment = (result.equipment as Record<string, unknown>[]).filter(
      eq => eq.id !== 'home'
    );
  }

  if (Array.isArray(result.inventory)) {
    result.inventory = (result.inventory as Record<string, unknown>[]).map(item => {
      if (item.fishId !== undefined) {
        return { type: 'fish', fishId: item.fishId, weight: item.weight };
      }
      return item;
    });
  }

  return result;
}

export function migrateV5ToV6(state: Record<string, unknown>): Record<string, unknown> {
  return {
    ...state,
    version: 6,
    vesselTrips: [],
  };
}

export function migrateV6ToV7(state: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = { ...state, version: 7 };

  if (Array.isArray(result.vessels)) {
    const oldVessels = result.vessels as Record<string, unknown>[];
    const newVessels: { vesselId: string; name: string }[] = [];
    for (const v of oldVessels) {
      const vid = (v.vesselId as string) || 'rowboat';
      const count = (v.count as number) || 0;
      for (let i = 0; i < count; i++) {
        newVessels.push({ vesselId: vid, name: `${vid}_${i + 1}` });
      }
    }
    result.vessels = newVessels;
  }

  if (Array.isArray(result.properties)) {
    const hasDock = (result.properties as Record<string, unknown>[]).some(p => p.id === 'dock');
    if (!hasDock) {
      result.properties = [...(result.properties as Record<string, unknown>[]), { id: 'dock', tier: 1 }];
    }
  }

  if (Array.isArray(result.discoveredLocations)) {
    const locs = result.discoveredLocations as string[];
    if (!locs.includes('harbor')) {
      result.discoveredLocations = [...locs, 'harbor'];
    }
    if (!locs.includes('dock')) {
      result.discoveredLocations = [...(result.discoveredLocations as string[]), 'dock'];
    }
  }

  return result;
}
